import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Avatar,
  Chip,
  Divider,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Paper,
  Tooltip,
  Fab,
  Alert,
  AlertTitle,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import {
  Close,
  PlayArrow,
  Stop,
  Add,
  Delete,
  Edit,
  DragIndicator,
  Image,
  VideoFile,
  AudioFile,
  Article,
  Web,
  Settings,
  AccessTime,
  VolumeUp,
  AspectRatio,
  Shuffle,
  Repeat,
  Visibility,
  WarningAmber,
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

import type { Playlist, PlaylistItem, Media } from '../../types';
import apiClient from '../../services/api';
import MediaSelectionDialog from './MediaSelectionDialog';
import PlaylistItemSettingsDialog from './PlaylistItemSettingsDialog';

// Локальный тип для результата drag & drop
interface DropResult {
  source: {
    index: number;
    droppableId: string;
  };
  destination?: {
    index: number;
    droppableId: string;
  } | null;
  draggableId: string;
}

interface PlaylistDetailsDialogProps {
  open: boolean;
  playlist: Playlist | null;
  onClose: () => void;
  onUpdate?: (playlist: Playlist) => void;
  onPreview?: (playlist: Playlist) => void;
}

interface PlaylistItemWithMedia extends PlaylistItem {
  media?: Media;
}

const PlaylistDetailsDialog: React.FC<PlaylistDetailsDialogProps> = ({
  open,
  playlist,
  onClose,
  onUpdate,
  onPreview,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<PlaylistItemWithMedia[]>([]);
  const [mediaDialogOpen, setMediaDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<PlaylistItemWithMedia | null>(null);
  const [itemSettingsOpen, setItemSettingsOpen] = useState(false);

  // Загрузка элементов плейлиста
  const loadPlaylistItems = async () => {
    if (!playlist) return;

    setLoading(true);
    try {
      const response = await apiClient.request('GET', `/playlists/${playlist.id}/items`);
      setItems(response);
    } catch (error) {
      console.error('Ошибка загрузки элементов плейлиста:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && playlist) {
      loadPlaylistItems();
    }
  }, [open, playlist]);

  // Обработка перетаскивания элементов
  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination || !playlist) return;

    const startIndex = result.source.index;
    const endIndex = result.destination.index;

    if (startIndex === endIndex) return;

    // Обновляем порядок локально
    const newItems = Array.from(items);
    const [reorderedItem] = newItems.splice(startIndex, 1);
    newItems.splice(endIndex, 0, reorderedItem);

    // Обновляем order_index для всех элементов
    const updatedItems = newItems.map((item, index) => ({
      ...item,
      order_index: index,
    }));

    setItems(updatedItems);

    // Отправляем изменения на сервер
    try {
      for (const item of updatedItems) {
        await apiClient.request('PUT', `/playlists/${playlist.id}/items/${item.id}`, {
          order_index: item.order_index,
        });
      }
    } catch (error) {
      console.error('Ошибка изменения порядка:', error);
      // Возвращаем исходный порядок при ошибке
      loadPlaylistItems();
    }
  };

  // Удаление элемента из плейлиста
  const handleDeleteItem = async (item: PlaylistItemWithMedia) => {
    if (!playlist) return;

    try {
      await apiClient.request('DELETE', `/playlists/${playlist.id}/items/${item.id}`);
      setItems(items.filter(i => i.id !== item.id));
    } catch (error) {
      console.error('Ошибка удаления элемента:', error);
    }
  };

  // Открытие настроек элемента
  const handleEditItem = (item: PlaylistItemWithMedia) => {
    setSelectedItem(item);
    setItemSettingsOpen(true);
  };

  // Получение иконки для типа медиа
  const getMediaIcon = (mediaType: string) => {
    switch (mediaType) {
      case 'image': return <Image />;
      case 'video': return <VideoFile />;
      case 'audio': return <AudioFile />;
      case 'pdf': return <Article />;
      case 'html': return <Web />;
      default: return <Article />;
    }
  };

  // Форматирование длительности
  const formatDuration = (duration: number) => {
    if (!duration) return 'N/A';
    const minutes = Math.floor(duration / 60);
    const seconds = Math.floor(duration % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Подсчет общей длительности плейлиста
  const getTotalDuration = () => {
    const total = items.reduce((sum, item) => {
      if (item.is_playable === false || item.media?.is_playable === false) return sum;
      const duration = item.custom_duration || item.media?.duration || 10; // 10 сек по умолчанию для изображений
      return sum + duration;
    }, 0);
    return formatDuration(total);
  };

  const brokenItemsCount = items.filter((item) => item.is_playable === false || item.media?.is_playable === false).length;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { minHeight: '70vh' }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h6" component="div">
                {playlist?.name || 'Плейлист'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('playlists.itemsCount', { count: items.length })} • {getTotalDuration()}
              </Typography>
              {brokenItemsCount > 0 && (
                <Typography variant="body2" color="error.main">
                  Нерабочих элементов: {brokenItemsCount}
                </Typography>
              )}
            </Box>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {/* Информация о плейлисте */}
          <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('common.description')}
                </Typography>
                <Typography variant="body1">
                  {playlist?.description || t('playlists.noDescription')}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Chip
                    size="small"
                    icon={playlist?.is_loop ? <Repeat /> : undefined}
                    label={playlist?.is_loop ? t('playlists.loopEnabled') : t('playlists.loopDisabled')}
                    color={playlist?.is_loop ? 'primary' : 'default'}
                  />
                  <Chip
                    size="small"
                    icon={playlist?.shuffle ? <Shuffle /> : undefined}
                    label={playlist?.shuffle ? t('playlists.shuffleEnabled') : t('playlists.shuffleDisabled')}
                    color={playlist?.shuffle ? 'secondary' : 'default'}
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Список элементов плейлиста */}
          {loading ? (
            <Box>
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} variant="rectangular" height={80} sx={{ mb: 1 }} />
              ))}
            </Box>
          ) : items.length === 0 ? (
            <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {t('playlists.noItems')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('playlists.noItemsDescription')}
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setMediaDialogOpen(true)}
              >
                {t('playlists.addFirstItem')}
              </Button>
            </Paper>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="playlist-items">
                {(provided) => (
                  <List
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    sx={{ width: '100%' }}
                  >
                    {items.map((item, index) => (
                      (() => {
                        const isBroken = item.is_playable === false || item.media?.is_playable === false;
                        return (
                      <Draggable
                        key={item.id}
                        draggableId={item.id.toString()}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <ListItem
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            sx={{
                              mb: 1,
                              bgcolor: isBroken ? 'error.light' : snapshot.isDragging ? 'action.hover' : 'background.paper',
                              border: 1,
                              borderColor: isBroken ? 'error.main' : 'divider',
                              borderRadius: 1,
                              opacity: isBroken ? 0.78 : 1,
                              '&:hover': {
                                bgcolor: 'action.hover',
                              },
                            }}
                          >
                            <ListItemAvatar>
                              <IconButton {...provided.dragHandleProps}>
                                <DragIndicator />
                              </IconButton>
                            </ListItemAvatar>

                            <ListItemAvatar>
                              <Avatar>
                                {isBroken ? <WarningAmber /> : getMediaIcon(item.media?.media_type || 'unknown')}
                              </Avatar>
                            </ListItemAvatar>

                            <ListItemText
                              primary={item.media?.title || item.media?.filename || 'Неизвестный файл'}
                              secondary={
                                <>
                                  <Typography variant="body2" color="text.secondary" component="span">
                                    {item.media?.media_type} • {formatDuration(item.custom_duration || item.media?.duration || 10)}
                                  </Typography>
                                  {item.custom_duration && (
                                    <Chip
                                      size="small"
                                      label={t('playlists.customDuration')}
                                      color="info"
                                      sx={{ mt: 0.5, ml: 1 }}
                                    />
                                  )}
                                  {isBroken && (
                                    <Chip
                                      size="small"
                                      label={item.availability_message || item.media?.availability_message || 'Файл недоступен'}
                                      color="error"
                                      sx={{ mt: 0.5, ml: 1 }}
                                    />
                                  )}
                                </>
                              }
                            />

                            <ListItemSecondaryAction>
                              <Tooltip title={t('common.settings')}>
                                <IconButton onClick={() => handleEditItem(item)}>
                                  <Settings />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t('common.delete')}>
                                <IconButton onClick={() => handleDeleteItem(item)} color="error">
                                  <Delete />
                                </IconButton>
                              </Tooltip>
                            </ListItemSecondaryAction>
                          </ListItem>
                        )}
                      </Draggable>
                        );
                      })()
                    ))}
                    {provided.placeholder}
                  </List>
                )}
              </Droppable>
            </DragDropContext>
          )}

          {/* Кнопка добавления элементов */}
          {items.length > 0 && (
            <Box display="flex" justifyContent="center" mt={2}>
              <Fab
                color="primary"
                aria-label={t('playlists.addMedia')}
                onClick={() => setMediaDialogOpen(true)}
              >
                <Add />
              </Fab>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>
            {t('common.close')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Visibility />}
            onClick={() => playlist && onPreview?.(playlist)}
            disabled={!playlist || !onPreview || (playlist.items_count || 0) === 0}
          >
            {t('playlists.preview')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог выбора медиафайлов */}
      <MediaSelectionDialog
        open={mediaDialogOpen}
        playlist={playlist}
        onClose={() => setMediaDialogOpen(false)}
        onMediaAdded={() => {
          loadPlaylistItems(); // Перезагружаем элементы плейлиста
          setMediaDialogOpen(false);
        }}
      />

      {/* Диалог настроек элемента */}
      <PlaylistItemSettingsDialog
        open={itemSettingsOpen}
        item={selectedItem}
        playlist={playlist}
        onClose={() => {
          setItemSettingsOpen(false);
          setSelectedItem(null);
        }}
        onSaved={() => {
          loadPlaylistItems(); // Перезагружаем элементы плейлиста
          setItemSettingsOpen(false);
          setSelectedItem(null);
        }}
      />
    </>
  );
};

export default PlaylistDetailsDialog;
